'use client';

import { ExitIcon } from '@radix-ui/react-icons';
import {
  NavigationMenu,
  NavigationMenuLink,
} from '@radix-ui/react-navigation-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import React from 'react';

import { Icons } from '@/app/_components/ui/icons';
import { PersonIcon } from '@/app/_componentsv2/icons/person-icon';
import {
  accountNav,
  accountNavItem,
  accountNavCta,
  accountNavItems,
  accountNavLink,
  accountNavLinkIcon,
  desktop,
} from '@/app/_componentsv2/layout/main/main-header.css';
import { tooltipContent } from '@/app/_componentsv2/ui.css';
import { User } from '@/domain/user/services/types';
import { shutdownIntercom } from '@/lib/intercom/client';
import generatePath from '@/lib/router/generate-path';
import Routes from '@/lib/router/routes';
import { pathnames } from '@/lib/translation/config';

import { logout } from './action';

export type MenuItem = {
  key: string;
  path: keyof typeof pathnames | Routes | string;
  label: string;
  Icon?: React.ReactNode;
  hide?: boolean;
};

type ConnectedMenuProps = {
  user: User | undefined;
  hasUserInvestmentToSign: boolean;
};

export const useConnectedMenu = ({
  user,
}: ConnectedMenuProps): Array<MenuItem> => {
  const t = useTranslations();

  const menuItems = [
    // Repointé directement vers Mon Espace V2 (évite le passage par les anciennes
    // pages /account qui provoquait un flash avant redirection).
    {
      key: 'portfolio',
      path: generatePath(Routes.Espace),
      label: t('header.menu.myEspace'),
      Icon: <Icons.Document />,
    },
    {
      key: 'favorite',
      path: generatePath(Routes.EspaceFavorites),
      label: t('header.menu.favorite'),
      Icon: <Icons.Star />,
    },
    {
      key: 'transactions',
      path: generatePath(Routes.EspaceWallet),
      label: t('header.menu.transactions'),
      Icon: <ExitIcon />,
    },
    {
      key: 'wallet',
      path: generatePath(Routes.EspaceWallet),
      label: t('header.menu.wallet'),
      Icon: <Icons.CreditCard />,
    },
    {
      key: 'rib',
      path: generatePath(Routes.EspaceAccount),
      label: t('header.menu.rib'),
      Icon: <Icons.Edit />,
    },
    {
      key: 'fiscalSituation',
      path: generatePath(Routes.EspaceAccount),
      label: t('header.menu.fiscalSituation'),
      Icon: <Icons.File />,
    },
    {
      key: 'company',
      path: generatePath(Routes.EspaceAccount),
      label: t('header.menu.company'),
      Icon: <Icons.Building />,
      hide: !user?.isCompany,
    },
    {
      key: 'details',
      path: generatePath(Routes.EspaceAccount),
      label: t('header.menu.details'),
      Icon: <Icons.Account />,
    },
  ];

  return menuItems.filter((menuItem) => !menuItem.hide);
};

export function ConnectedMenu(props: ConnectedMenuProps) {
  const pathname = usePathname();
  const { user, hasUserInvestmentToSign } = props;

  const connectedMenu = useConnectedMenu(props);
  const t = useTranslations();

  const [portfolioMenuItem] = connectedMenu;

  const searchParams =
    Routes.Home !== pathname
      ? `?${new URLSearchParams({ origin: pathname }).toString()}`
      : '';

  const handleLogout = async () => {
    // Here we need to manually call the intercom shutdown, because it must remove its own cookies before logging out the user
    shutdownIntercom();
    await logout();
  };

  if (!user) {
    return (
      <nav className={clsx(accountNav, desktop)} aria-label="Account">
        <ul className={accountNavItems}>
          <li className={accountNavItem}>
            <Link
              className={accountNavLink}
              href={`${Routes.SignIn}${searchParams}`}
            >
              <PersonIcon className={accountNavLinkIcon} />
              <span>{t('auth.signIn.title')}</span>
            </Link>
          </li>
          <li className={accountNavItem}>
            <Link
              className={accountNavCta}
              href={`${Routes.SignUpRegister}${searchParams}`}
              rel="nofollow"
            >
              {t('auth.signUp.createAccount')}
            </Link>
          </li>
        </ul>
      </nav>
    );
  }

  const portfolio = hasUserInvestmentToSign ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavigationMenuLink asChild className={accountNavLink}>
            <Link href={portfolioMenuItem.path}>{portfolioMenuItem.label}</Link>
          </NavigationMenuLink>
        </TooltipTrigger>
        <TooltipContent className={tooltipContent} align="end" side="bottom">
          {t('header.menu.portfolioTooltip')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    // `asChild` + Link Next : navigation client (préchargement au survol, squelette
    // `loading.tsx` immédiat) au lieu d'un <a> brut qui rechargeait toute la page sans
    // retour visuel — en local, le premier clic semblait ne rien faire le temps de compiler
    // la route.
    <NavigationMenuLink asChild className={accountNavLink}>
      <Link href={portfolioMenuItem.path}>{portfolioMenuItem.label}</Link>
    </NavigationMenuLink>
  );

  // `desktop` (display: none sous 1024 px) sur un conteneur à part : combiné à `accountNavItems`
  // (display: flex) sur le même élément, le flex l'emportait et le menu apparaissait sur mobile.
  return (
    <div className={clsx(accountNav, desktop)}>
      <NavigationMenu className={accountNavItems} aria-label="Account">
        {portfolio}
        {/* Plus de menu déroulant « Prénom N. » (doublon de Mon Espace + onglets périmés) :
            on garde uniquement « Mon Espace » et un bouton de déconnexion direct. */}
        <button className={accountNavLink} onClick={() => handleLogout()}>
          {t('auth.logout')}
        </button>
      </NavigationMenu>
    </div>
  );
}
